import { motion } from "framer-motion";
import { useState } from "react";
import { ArrowRight } from "lucide-react";

const showcase = [
  {
    category: "Tesla",
    title: "Model S Plaid",
    description: "The ultimate performance sedan with tri-motor drive and 200+ mph capability.",
    specs: ["0-60 in 1.99s", "4 seats", "Range: 650+ miles"],
    image: "⚡",
  },
  {
    category: "Tesla",
    title: "Cybertruck",
    description: "The future of trucks. Stainless steel exoskeleton with revolutionary design.",
    specs: ["Dual / Tri Motor", "Towing: 14,000 lbs", "Range: 500+ miles"],
    image: "🔶",
  },
  {
    category: "Tesla",
    title: "Model 3",
    description: "The most affordable Tesla. Safety, efficiency, and performance combined.",
    specs: ["0-60 in 3.2s", "5 seats", "Range: 300+ miles"],
    image: "🚗",
  },
  {
    category: "SpaceX",
    title: "Starship",
    description: "Fully reusable super heavy-lift launch vehicle designed for Mars missions.",
    specs: ["100 ton payload", "33 Raptor engines", "1,000 ft tall"],
    image: "🚀",
  },
  {
    category: "SpaceX",
    title: "Falcon 9",
    description: "World's first orbital class reusable rocket. 15+ years of proven reliability.",
    specs: ["Reusable 1st stage", "50+ launches", "100% success rate"],
    image: "🛸",
  },
  {
    category: "Innovation",
    title: "Starlink",
    description: "Global satellite internet constellation providing low-latency worldwide connectivity.",
    specs: ["50,000+ satellites", "1 Gbps+ speeds", "Polar coverage"],
    image: "🛰️",
  },
];

export default function TeslaSpaceXShowcase() {
  const [selected, setSelected] = useState(0);

  const currentItem = showcase[selected];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
    },
  };

  const contentVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, delay: 0.2 },
    },
    exit: {
      opacity: 0,
      x: -20,
      transition: { duration: 0.3 },
    },
  };
  return (
    <motion.div
      className="space-y-12"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
    >
      {/* Main Showcase */}
      <motion.div
        key={selected}
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        className="relative"
      >
        <div className="border border-border p-8 md:p-12 flex flex-col md:flex-row gap-12 items-center">
          <motion.div
            className="flex-shrink-0 text-6xl md:text-8xl opacity-30"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 4, repeat: Infinity }}
          >
            {currentItem.image}
          </motion.div>
          <motion.div className="flex-1" variants={contentVariants}>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-4">
                {currentItem.category}
              </p>
              <h3 className="text-3xl md:text-4xl font-medium text-foreground mb-4">
                {currentItem.title}
              </h3>
              <p className="text-base text-muted-foreground mb-8 leading-relaxed max-w-lg">
                {currentItem.description}
              </p>
              <motion.div
                className="grid grid-cols-3 gap-6"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {currentItem.specs.map((spec, idx) => (
                  <motion.div key={idx} variants={itemVariants}>
                    <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">
                      Spec {idx + 1}
                    </p>
                    <p className="font-medium text-foreground">{spec}</p>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      {/* Carousel Grid */}
      <motion.div variants={itemVariants}>
        <p className="text-sm font-medium text-muted-foreground uppercase tracking-widest mb-6">
          Select Project
        </p>
        <motion.div
          className="grid grid-cols-2 md:grid-cols-3 gap-4"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          {showcase.map((item, idx) => (
            <motion.button
              key={idx}
              onClick={() => setSelected(idx)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              variants={itemVariants}
              className={`p-6 border text-left transition-all ${
                idx === selected
                  ? "border-foreground bg-card"
                  : "border-border hover:border-foreground/50"
              }`}
            >
              <p className="text-3xl mb-3">{item.image}</p>
              <p className="text-sm font-medium text-foreground">{item.title}</p>
              <p className="text-xs text-muted-foreground mt-1">{item.category}</p>
            </motion.button>
          ))}
        </motion.div>
      </motion.div>
    </motion.div>
  );
}